/**
 * Effective Date Validation Utility
 *
 * Feature: 005-price-grade-timeline
 * Tasks: T015b, T022b
 * Description: Validates that effectiveDate is after the last concluded work date.
 *              This prevents creating price/grade versions that would retroactively
 *              affect already concluded (finalized) work records.
 */
import { database } from '@/plugins/firebase';
import { CURRENT_SEASON_YEAR } from '@/store/seasonYear';
import moment from 'moment';
const WorksCollection = 'works' + CURRENT_SEASON_YEAR;
/**
 * Get the date of the last concluded work record.
 * Returns null if no concluded work exists.
 *
 * A concluded work record is one where `concluded === true`,
 * indicating that the day's work has been finalized and should
 * not be affected by price/grade changes.
 *
 * Note: This query avoids requiring a composite index by fetching
 * all concluded records and finding the max date client-side.
 * At typical scale (~365 records/year), this is acceptable.
 *
 * @returns Promise resolving to ISO date string (YYYY-MM-DD) or null
 */
export async function getLastConcludedWorkDate() {
    const snapshot = await database
        .collection(WorksCollection)
        .where('concluded', '==', true)
        .get();
    if (snapshot.empty) {
        return null;
    }
    // Find the maximum date client-side to avoid composite index requirement
    let maxDate = null;
    snapshot.docs.forEach(doc => {
        const date = doc.data().date;
        if (!maxDate || date > maxDate) {
            maxDate = date;
        }
    });
    return maxDate;
}
/**
 * Validate that an effectiveDate is valid for a new price/grade version.
 * The effectiveDate must be after the last concluded work date.
 *
 * Business rule: Once a work day is concluded, its pricing and grades
 * are locked. Any new price/grade versions must have an effectiveDate
 * that is strictly after the last concluded date.
 *
 * @param effectiveDate - ISO date string (YYYY-MM-DD)
 * @returns Promise resolving to validation result with isValid boolean and optional error message
 */
export async function validateEffectiveDate(effectiveDate) {
    // Runtime type guard (L2 security fix)
    if (typeof effectiveDate !== 'string' || !effectiveDate) {
        return {
            isValid: false,
            error: 'Datum platnosti je povinné.',
        };
    }
    // Validate date format (YYYY-MM-DD) - T033
    const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateFormatRegex.test(effectiveDate)) {
        return {
            isValid: false,
            error: 'Neplatný formát data. Použijte formát YYYY-MM-DD.',
        };
    }
    // Also validate it's a real date (not 2025-13-45)
    const parsedDate = moment(effectiveDate, 'YYYY-MM-DD', true);
    if (!parsedDate.isValid()) {
        return {
            isValid: false,
            error: 'Neplatné datum.',
        };
    }
    const lastConcludedDate = await getLastConcludedWorkDate();
    // If no concluded work exists, any date is valid
    if (!lastConcludedDate) {
        return { isValid: true };
    }
    // effectiveDate must be AFTER the last concluded date (strictly greater)
    if (effectiveDate <= lastConcludedDate) {
        const formattedDate = moment(lastConcludedDate).format('DD.MM.YYYY');
        return {
            isValid: false,
            error: `Datum platnosti musí být po ${formattedDate} (poslední uzavřený den)`,
        };
    }
    return { isValid: true };
}
/**
 * Validate effectiveDate and throw an error if invalid.
 * Convenience wrapper for use in Vuex actions.
 *
 * @param effectiveDate - ISO date string (YYYY-MM-DD) or undefined
 * @throws Error with Czech message if validation fails
 */
export async function validateEffectiveDateOrThrow(effectiveDate) {
    const validation = await validateEffectiveDate(effectiveDate || '');
    if (!validation.isValid) {
        throw new Error(validation.error);
    }
}
/**
 * Validate that a record can be deleted based on its effectiveDate.
 * Records can only be deleted if their effectiveDate is AFTER the last concluded work date.
 *
 * Business rule: Once a work day is concluded, any price/grade versions that were
 * effective on or before that date are locked and cannot be deleted. This ensures
 * historical data integrity.
 *
 * Legacy records without effectiveDate are treated as having effectiveDate of 1970-01-01,
 * making them protected from deletion once any work is concluded.
 *
 * @param effectiveDate - ISO date string (YYYY-MM-DD) or undefined
 * @returns Promise resolving to validation result with isValid boolean and optional error message
 */
export async function validateDeleteEffectiveDate(effectiveDate) {
    // Treat missing effectiveDate as epoch date (legacy data)
    const dateToValidate = effectiveDate || '1970-01-01';
    const lastConcludedDate = await getLastConcludedWorkDate();
    // If no concluded work exists, any record can be deleted
    if (!lastConcludedDate) {
        return { isValid: true };
    }
    // Record can only be deleted if effectiveDate is strictly AFTER last concluded date
    if (dateToValidate <= lastConcludedDate) {
        const formattedEffectiveDate = effectiveDate
            ? moment(effectiveDate).format('DD.MM.YYYY')
            : 'bez data (starý záznam)';
        const formattedConcludedDate = moment(lastConcludedDate).format('DD.MM.YYYY');
        return {
            isValid: false,
            error: `Nelze smazat záznam s datem platnosti ${formattedEffectiveDate}. Záznamy lze mazat pouze pokud jejich datum platnosti je po posledním uzavřeném dni (${formattedConcludedDate}).`,
        };
    }
    return { isValid: true };
}
/**
 * Validate delete operation and throw an error if invalid.
 * Convenience wrapper for use in Vuex actions.
 *
 * @param effectiveDate - ISO date string (YYYY-MM-DD) or undefined
 * @throws Error with Czech message if deletion is not allowed
 */
export async function validateDeleteOrThrow(effectiveDate) {
    const validation = await validateDeleteEffectiveDate(effectiveDate);
    if (!validation.isValid) {
        throw new Error(validation.error);
    }
}
